import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CountTextField from "crqlar-react-common/dist/components/CountTextField";
import Property from "crqlar-react-common/dist/components/Property";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import type {DateRange} from "../../../common/newDatePicker/datePickerShared";
import {RangeDatePicker} from "../../../common/newDatePicker/RangeDatePicker";
import {BUDGET_SCREEN_HEIGHT, BudgetManagerHeader} from "./BudgetManagerHeader";
import type {InsightTargetPeriod} from "./insightTargets";
import {TARGET_METRIC_DEFINITIONS, type InsightTargetMetricDefinition} from "./targetDefinitions";
import {useRemoveInsightTarget, useSaveInsightTargets} from "./useInsightTargets";
import {buildRequest} from "../useInsightsV2Stats";
import {insightCurrency, insightCurrencySymbol} from "../base/insightCurrency";

type StatTargetDraft = Record<string, string>;

/** The two groups differ in how a budget is judged, which is what each group's lead line explains. */
const RATE_METRICS = TARGET_METRIC_DEFINITIONS.filter(definition => definition.kind === "RATE");
const CUMULATIVE_METRICS = TARGET_METRIC_DEFINITIONS.filter(definition => definition.kind === "CUMULATIVE");

const monthPeriod = (year: number, month: number): DateRange => ({
    start: new Date(year, month, 1),
    end: new Date(year, month + 1, 0),
});

const currentMonthPeriod = () => {
    const today = new Date();
    return monthPeriod(today.getFullYear(), today.getMonth());
};

const yearPeriod = (year: number): DateRange => ({
    start: new Date(year, 0, 1),
    end: new Date(year, 11, 31),
});

const parseIsoDate = (value: string) => new Date(`${value}T00:00:00`);

const periodRange = (period?: InsightTargetPeriod): DateRange => period
    ? {start: parseIsoDate(period.from), end: parseIsoDate(period.to)}
    : currentMonthPeriod();

const periodDraft = (period?: InsightTargetPeriod): StatTargetDraft => (
    TARGET_METRIC_DEFINITIONS.reduce<StatTargetDraft>((draft, {id}) => ({
        ...draft,
        [id]: period?.targets[id]?.toString() ?? "",
    }), {})
);

interface InsightsTargetEditorProps {
    open: boolean;
    period?: InsightTargetPeriod;
    existingPeriods: InsightTargetPeriod[];
    onBack: () => void;
    onClose: () => void;
    onSaved: () => void;
}

export function InsightsTargetEditor({
    open,
    period,
    existingPeriods,
    onBack,
    onClose,
    onSaved,
}: InsightsTargetEditorProps) {
    const {t, i18n} = useTranslation();
    const currency = insightCurrency(useOrganization()?.currencyCode);
    const initialTargetName = period?.name?.trim() || t(period ? "Budget" : "New budget");
    const [targetName, setTargetName] = useState(initialTargetName);
    const [targetDraft, setTargetDraft] = useState<StatTargetDraft>(() => periodDraft(period));
    const [targetPeriod, setTargetPeriod] = useState<DateRange>(() => periodRange(period));
    const [monthMenuAnchor, setMonthMenuAnchor] = useState<HTMLElement | null>(null);
    const [yearMenuAnchor, setYearMenuAnchor] = useState<HTMLElement | null>(null);
    const [removeConfirmationOpen, setRemoveConfirmationOpen] = useState(false);
    const saveTargetsMutation = useSaveInsightTargets();
    const removeTargetMutation = useRemoveInsightTarget();
    const resetSaveTargets = saveTargetsMutation.reset;
    const resetRemoveTarget = removeTargetMutation.reset;
    const saving = saveTargetsMutation.isLoading;
    const removing = removeTargetMutation.isLoading;

    useEffect(() => {
        if (!open) return;
        setTargetName(period?.name?.trim() || t(period ? "Budget" : "New budget"));
        setTargetDraft(periodDraft(period));
        setTargetPeriod(periodRange(period));
        setMonthMenuAnchor(null);
        setYearMenuAnchor(null);
        setRemoveConfirmationOpen(false);
        resetSaveTargets();
        resetRemoveTarget();
    }, [open, period, resetRemoveTarget, resetSaveTargets, t]);

    const selectTargetPeriod = useCallback((value: DateRange) => setTargetPeriod(value), []);
    const saveTargets = () => {
        const targets = TARGET_METRIC_DEFINITIONS.reduce<Record<string, number | null>>((values, {id}) => {
            const raw = targetDraft[id]?.trim() ?? "";
            const value = Number(raw);
            values[id] = raw && Number.isFinite(value) && value > 0 ? value : null;
            return values;
        }, {});
        const request = buildRequest(targetPeriod);

        saveTargetsMutation.mutate({
            ...request,
            name: targetName.trim(),
            targets,
            ...(period ? {originalFrom: period.from, originalTo: period.to} : {}),
        }, {onSuccess: onSaved});
    };
    const removeTarget = () => {
        if (period == null) return;

        removeTargetMutation.mutate({from: period.from, to: period.to}, {
            onSuccess: () => {
                setRemoveConfirmationOpen(false);
                onSaved();
            },
        });
    };

    const locale = i18n.resolvedLanguage || i18n.language;
    const monthFormatter = useMemo(() => new Intl.DateTimeFormat(locale, {month: "long"}), [locale]);
    const monthYearFormatter = useMemo(
        () => new Intl.DateTimeFormat(locale, {month: "long", year: "numeric"}),
        [locale],
    );
    const currencySymbol = useMemo(() => insightCurrencySymbol(currency, locale), [currency, locale]);
    const targetYears = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const selectedYear = targetPeriod.start.getFullYear();
        const around = (year: number) => Array.from({length: 5}, (_, offset) => year + offset - 2);
        return Array.from(new Set([...around(currentYear), ...around(selectedYear)])).sort((left, right) => left - right);
    }, [targetPeriod.start]);
    const selectedPeriodRequest = buildRequest(targetPeriod);
    const collidingPeriod = existingPeriods.find(existing => (
        existing.id === `${selectedPeriodRequest.from}:${selectedPeriodRequest.to}` && existing.id !== period?.id
    ));

    const budgetRow = (definition: InsightTargetMetricDefinition) => {
        const unit = definition.format === "CURRENCY"
            ? currencySymbol
            : definition.format === "PERCENT" ? "%" : undefined;

        return (
            <Property
                key={definition.id}
                title={unit == null ? t(definition.title) : `${t(definition.title)} (${unit})`}
                tooltip={t(definition.hint)}
            >
                <CountTextField
                    id={`budget-${definition.id}`}
                    size={"small"}
                    value={targetDraft[definition.id] ?? ""}
                    onChangeValue={value => setTargetDraft(draft => ({
                        ...draft,
                        [definition.id]: value == null || Number.isNaN(value) ? "" : String(value),
                    }))}
                    InputProps={{inputProps: {min: 0, max: definition.format === "PERCENT" ? 100 : undefined}}}
                    disabled={saving}
                    sx={{width: 168}}
                />
            </Property>
        );
    };

    const quickSelectSx = {textTransform: "none", color: "text.primary"} as const;

    return (
        <>
            <BudgetManagerHeader
                title={targetName.trim() || t("New budget")}
                onBack={onBack}
                onClose={onClose}
                disabled={saving}
            />

            <DialogContent dividers sx={{p: 3, height: BUDGET_SCREEN_HEIGHT}}>
                <Stack gap={1}>
                    {saveTargetsMutation.isError && (
                        <Alert severity={"error"} sx={{mb: 1}}>
                            {(saveTargetsMutation.error as {response?: {data?: {message?: string}}})
                                ?.response?.data?.message ?? t("Could not save the budgets.")}
                        </Alert>
                    )}

                    <Property title={t("Budget name")}>
                        <TextField
                            id={"budget-name"}
                            size={"small"}
                            value={targetName}
                            onChange={event => setTargetName(event.target.value)}
                            inputProps={{maxLength: 120}}
                            disabled={saving}
                            fullWidth
                            autoFocus
                        />
                    </Property>

                    <Property
                        title={t("Budget period")}
                        tooltip={t("Every budget in this list is judged against this period. Two budgets cannot cover the same range.")}
                    >
                        <Stack direction={"row"} alignItems={"center"} gap={1} flexWrap={"wrap"}>
                            <RangeDatePicker
                                size={"small"}
                                value={targetPeriod}
                                onChange={selectTargetPeriod}
                                showWeekday={false}
                                showStepButtons={false}
                                showTodayButton={false}
                                disabled={saving}
                            />

                            <Button
                                size={"small"}
                                endIcon={<ArrowDropDownIcon/>}
                                onClick={event => setMonthMenuAnchor(event.currentTarget)}
                                aria-haspopup={"menu"}
                                aria-expanded={Boolean(monthMenuAnchor)}
                                disabled={saving}
                                sx={quickSelectSx}
                            >
                                {t("Month")}: {monthYearFormatter.format(targetPeriod.start)}
                            </Button>

                            <Button
                                size={"small"}
                                endIcon={<ArrowDropDownIcon/>}
                                onClick={event => setYearMenuAnchor(event.currentTarget)}
                                aria-haspopup={"menu"}
                                aria-expanded={Boolean(yearMenuAnchor)}
                                disabled={saving}
                                sx={quickSelectSx}
                            >
                                {t("Full year")}: {targetPeriod.start.getFullYear()}
                            </Button>
                        </Stack>
                    </Property>

                    {collidingPeriod && (
                        <Alert severity={"warning"} sx={{mt: 1}}>
                            {t("A budget already exists for this period. Edit it from the budget list instead.")}
                        </Alert>
                    )}

                    <Divider sx={{my: 1.5}}/>

                    <Typography variant={"body2"} color={"text.secondary"} sx={{mb: 0.5}}>
                        {t("Each budget applies as-is to any range inside the budget period. Leave a field empty to set no budget.")}
                    </Typography>

                    {RATE_METRICS.map(budgetRow)}

                    <Divider sx={{my: 1.5}}/>

                    <Typography variant={"body2"} color={"text.secondary"} sx={{mb: 0.5}}>
                        {t("These are totals for the whole budget period — a shorter range on screen is judged against a proportional share.")}
                    </Typography>

                    {CUMULATIVE_METRICS.map(budgetRow)}
                </Stack>
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                {period != null && (
                    <Button
                        color={"error"}
                        disabled={saving || removing}
                        onClick={() => setRemoveConfirmationOpen(true)}
                        sx={{mr: "auto"}}
                    >
                        {t("Delete")}
                    </Button>
                )}

                <Button color={"inherit"} onClick={onBack} disabled={saving}>
                    {t("Cancel")}
                </Button>

                <Button
                    variant={"contained"}
                    onClick={saveTargets}
                    disabled={!targetName.trim() || saving || Boolean(collidingPeriod)}
                    sx={{minWidth: 96}}
                >
                    {period ? t("Save changes") : t("Create budget")}
                </Button>
            </DialogActions>

            <Menu
                anchorEl={monthMenuAnchor}
                open={Boolean(monthMenuAnchor)}
                onClose={() => setMonthMenuAnchor(null)}
                MenuListProps={{"aria-label": t("Month")}}
                PaperProps={{sx: {maxHeight: 360, minWidth: 180}}}
            >
                {Array.from({length: 12}, (_, month) => (
                    <MenuItem
                        key={month}
                        selected={targetPeriod.start.getMonth() === month}
                        onClick={() => {
                            selectTargetPeriod(monthPeriod(targetPeriod.start.getFullYear(), month));
                            setMonthMenuAnchor(null);
                        }}
                    >
                        {monthFormatter.format(new Date(targetPeriod.start.getFullYear(), month, 1))}
                    </MenuItem>
                ))}
            </Menu>

            <Menu
                anchorEl={yearMenuAnchor}
                open={Boolean(yearMenuAnchor)}
                onClose={() => setYearMenuAnchor(null)}
                MenuListProps={{"aria-label": t("Full year")}}
                PaperProps={{sx: {minWidth: 180}}}
            >
                {targetYears.map(year => (
                    <MenuItem
                        key={year}
                        selected={targetPeriod.start.getFullYear() === year}
                        onClick={() => {
                            selectTargetPeriod(yearPeriod(year));
                            setYearMenuAnchor(null);
                        }}
                    >
                        {year}
                    </MenuItem>
                ))}
            </Menu>

            <Dialog
                open={removeConfirmationOpen}
                onClose={() => !removing && setRemoveConfirmationOpen(false)}
                maxWidth={"sm"}
                fullWidth
                aria-labelledby={"remove-insight-target-title"}
            >
                <DialogTitle id={"remove-insight-target-title"}>{t("Delete budget?")}</DialogTitle>

                <DialogContent>
                    <Alert severity={"warning"}>
                        {t("{{name}} is permanently removed and the metrics it covered are no longer judged against a budget.", {
                            name: targetName.trim() || t("This budget"),
                        })}
                    </Alert>

                    {removeTargetMutation.isError && (
                        <Alert severity={"error"} sx={{mt: 2}}>{t("Could not remove the budget.")}</Alert>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button color={"inherit"} disabled={removing} onClick={() => setRemoveConfirmationOpen(false)}>
                        {t("Cancel")}
                    </Button>

                    <Button color={"error"} variant={"contained"} disabled={removing} onClick={removeTarget}>
                        {t("Delete")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default InsightsTargetEditor;
